import cloneDeep from "lodash/cloneDeep";
import orderBy from "lodash/orderBy";
import { createSelector } from "reselect";

import { parseName } from "../../shared/utils";
import groupsSelectors from "./groups";
import policiesSelectors from "./policies";
import usersSelectors from "./users";

const entities = {};

entities.selectMatchedName = (state, props) => {
  if (props.match) {
    return props.match.params.name;
  }
  return undefined;
};

/**
 * Return list of groups and users matching Matched name
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getMatched = createSelector(
  [groupsSelectors.get, usersSelectors.get, entities.selectMatchedName],
  (groups, users, matchedName) => {
    const entities = [...groups, ...users];
    const matched = entities.find(
      entity => entity.username === matchedName || entity.name === matchedName
    );
    if (matched) {
      return {
        $uri: matched.$uri,
        type: matched.username ? "user" : "group",
        name: matched.username || matched.name,
        email: matched.email || "N/A",
        policies: []
      };
    }
    return undefined;
  }
);

/**
 * Return list of groups and users formatted for policies table and group list
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.get = createSelector(
  [groupsSelectors.get, usersSelectors.get, policiesSelectors.get],
  (groups, users, policies) => {
    const entities = [...groups, ...users];

    return entities.map(entity => {
      const type = entity.username ? "user" : "group";
      const filteredPolicies = policies.filter(policy =>
        policy[type] ? policy[type].$ref === entity.$uri : undefined
      );
      return {
        $uri: entity.$uri,
        type,
        name: entity.username || entity.name,
        email: entity.email || "N/A",
        policies: filteredPolicies
      };
    });
  }
);

/**
 * Return list of entities with policies in the current service
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getUsingCurrentService = createSelector(
  [entities.get, policiesSelectors.getAllByCurrentService],
  (entities, policies) => {
    const policyUris = policies.map(policy => policy.$uri);
    const filteredEntities = entities.filter(entity =>
      entity.policies.some(policy => policyUris.includes(policy.$uri))
    );

    return orderBy(
      filteredEntities,
      [entity => entity.type === "group", entity => entity.name.toLowerCase()],
      ["desc", "asc"]
    );
  }
);

/**
 * Return list of entities with policies in scope and filtered by filter term
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getFiltered = createSelector(
  [
    groupsSelectors.getFiltered,
    usersSelectors.getFiltered,
    policiesSelectors.getInScope
  ],
  (groups, users, policies) => {
    const entities = [...groups, ...users];
    return entities.map(entity => {
      const type = entity.username ? "user" : "group";
      const filteredPolicies = policies.filter(policy =>
        policy[type] ? policy[type].$ref === entity.$uri : undefined
      );
      return {
        $uri: entity.$uri,
        type,
        name: entity.username || entity.name,
        domain: entity.domain,
        email: entity.email,
        policies: filteredPolicies
      };
    });
  }
);

/**
 * Return filtered entities sorted by name
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getByName = createSelector(
  [entities.getFiltered],
  entities => {
    const sortedEntities = orderBy(
      entities,
      [entity => entity.name.toLowerCase()],
      ["asc"]
    );
    return sortedEntities;
  }
);

/**
 * Return filtered entities sorted by domain
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getByDomain = createSelector(
  [entities.getFiltered],
  entities => {
    const entitiesWithDomain = cloneDeep(entities);
    const sortedEntities = orderBy(
      entitiesWithDomain.map(entity => {
        const thisEntity = entity;
        const name = thisEntity.name || thisEntity.username;
        const parsed = parseName(name);
        thisEntity.domain = parsed.domain;
        return thisEntity;
      }),
      [
        entity => entity.domain && entity.domain.toLowerCase(),
        entity => entity.name.toLowerCase()
      ],
      ["asc"]
    );
    return sortedEntities;
  }
);

/**
 * Return filtered entities sorted by group type first
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getByGroup = createSelector(
  [entities.getFiltered],
  entities => {
    const sortedEntities = orderBy(
      entities,
      [entity => entity.type === "group", entity => entity.name.toLowerCase()],
      ["desc", "asc"]
    );
    return sortedEntities;
  }
);

/**
 * Return filtered entities sorted by user type first
 * @param {Object} state
 * @returns {Object[]} entities
 */
entities.getByUser = createSelector(
  [entities.getFiltered],
  entities => {
    const sortedEntities = orderBy(
      entities,
      [entity => entity.type === "user", entity => entity.name.toLowerCase()],
      ["desc", "asc"]
    );
    return sortedEntities;
  }
);

export default entities;
